import {
    Component,
    computed,
    inject,
    model,
    OnInit,
    signal,
} from '@angular/core';
import {
    MatFormField,
    MatLabel,
    MatHint,
    MatError,
} from '@angular/material/form-field';
import {
    MatDatepickerToggle,
    MatDatepickerInputEvent,
    MatDateRangeInput,
    MatDateRangePicker,
    MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatDialogRef, MatDialogContent } from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { DialogHeaderComponent } from '../dialog-header/dialog-header.component';
import { MatDivider } from '@angular/material/divider';
import { AdminTimesheet } from 'src/app/models/admin-timesheet.model';
import { Page } from 'src/app/models/page.model';
import {
    COUNT,
    DATE,
    ENDDATE,
    OFFSET,
    STARTDATE,
    TYPE,
    USERPROFILEID,
    WEEK,
    YEAR,
} from 'src/app/constants/queryParamConstants';
import { COUNT_ALL, OFFSET_START } from 'src/app/constants/searchConstants';
import { AdminTimesheetService } from 'src/app/services/admin-timesheet.service';
import { getISOWeek } from 'date-fns';
import {
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
} from '@angular/forms';
import { NgFor, NgIf } from '@angular/common';
import { MatInput } from '@angular/material/input';
import { MatOption } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import {
    MatAutocomplete,
    MatAutocompleteModule,
    MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { ReportsService } from 'src/app/services/reports.service';
import { Subscription } from 'rxjs';
import { saveAs } from 'file-saver';
import { UniversalSnackbarService } from 'src/app/services/universal-snackbar.service';
import { SnackbarMessages as MSG } from 'src/app/enums/snackBar.enum';
import { PDF, CSV } from 'src/app/constants/queryParamConstants';
import {
    ADMINUSERFILENAME,
    CSVMIMETYPE,
    PDFMIMETYPE,
} from 'src/app/constants/exportConstants';
import {
    MatChipGrid,
    MatChipInputEvent,
    MatChipRow,
    MatChipsModule,
} from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { User } from 'src/app/models/User';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { maxExportUsers } from 'src/app/constants/timesheetConstants';

interface IFilterFormControls {
    date: FormControl<Date | null>;
    projectId: FormControl<string | null>;
    taskId: FormControl<string | null>;
    fullName: FormControl<string | null>;
    clientId: FormControl<string | null>;
    hours: FormControl<string | number | null>;
    salesRepId: FormControl<string | null>;
    recruiterId: FormControl<string | null>;
}

@Component({
    selector: 'app-admin-timesheet-dialog',
    standalone: true,
    imports: [
        MatButton,
        MatRadioButton,
        MatRadioGroup,
        DialogHeaderComponent,
        MatDivider,
        MatDialogContent,
        MatLabel,
        MatDateRangeInput,
        MatFormField,
        MatHint,
        MatDatepickerToggle,
        MatDateRangePicker,
        MatError,
        MatDatepickerModule,
        NgIf,
        ReactiveFormsModule,
        FormsModule,
        MatInput,
        MatOption,
        MatSelectModule,
        MatAutocomplete,
        MatAutocompleteModule,
        MatChipGrid,
        MatChipRow,
        MatIcon,
        MatChipsModule,
        MatTooltipModule,
        MatCardModule,
    ],
    templateUrl: './admin-timesheet-dialog.component.html',
    styleUrl: './admin-timesheet-dialog.component.scss',
})
export class AdminTimesheetDialogComponent implements OnInit {
    reportLoading: boolean = true;
    CSV: string = CSV;
    PDF: string = PDF;
    queryParams: Map<string, string | boolean> = new Map<
        string,
        string | boolean
    >();
    searchControl: FormControl<AdminTimesheet> = new FormControl();
    formatControl: FormControl<string> = new FormControl();
    loadingUsers: boolean = false;
    users: AdminTimesheet[] = [];
    maxExportUsers: number = maxExportUsers;
    isDateRangeMode: boolean = false;

    dateRangeForm: FormGroup<{
        startDate: FormControl<Date>;
        endDate: FormControl<Date>;
        date: FormControl<Date>;
    }> = new FormGroup({
        startDate: new FormControl(new Date(), { nonNullable: true }),
        endDate: new FormControl(new Date(), { nonNullable: true }),
        date: new FormControl(new Date(), { nonNullable: true }),
    });

    filterForm: FormGroup<IFilterFormControls> = new FormGroup({
        date: new FormControl(new Date()),
        clientId: new FormControl(),
        projectId: new FormControl(),
        taskId: new FormControl(),
        fullName: new FormControl(),
        hours: new FormControl(),
        salesRepId: new FormControl(),
        recruiterId: new FormControl(),
    });

    ngOnInit(): void {
        this.getReports();
        const params: Map<string, number> = new Map<string, number>();
        params.set(COUNT, COUNT_ALL);
    }

    constructor(
        public dialogRef: MatDialogRef<AdminTimesheetDialogComponent>,
        private adminTimesheetService: AdminTimesheetService,
        private reportService: ReportsService,
        private snackBar: UniversalSnackbarService,
        private adminUserService: AdminTimesheetService,
    ) {}

    dateTypeChange(): void {
        this.queryParams.delete(STARTDATE);
        this.queryParams.delete(ENDDATE);
        this.setQueryParams();
        this.getReports();
    }

    dateChange(event: MatDatepickerInputEvent<Date>): void {
        if (
            this.dateRangeForm.controls.startDate.value &&
            this.dateRangeForm.controls.endDate.value &&
            this.isDateRangeMode
        ) {
            this.filterForm.controls.date.setValue(
                this.dateRangeForm.controls.startDate.value,
            );

            this.getReports();
        } else if (
            !this.isDateRangeMode &&
            this.dateRangeForm.controls.date.value
        ) {
            this.filterForm.controls.date.setValue(event.value);
            this.getReports();
        }
    }

    setQueryParams(): void {
        if (this.isDateRangeMode) {
            const startDate: Date = this.dateRangeForm.get(STARTDATE)?.value;
            const endDate: Date = this.dateRangeForm.get(ENDDATE)?.value;

            if (startDate && endDate) {
                this.queryParams = this.queryParams
                    .set(STARTDATE, startDate.toISOString())
                    .set(ENDDATE, endDate.toISOString());
            }
        } else if (!this.isDateRangeMode) {
            const selectedDate: Date = this.dateRangeForm.get(DATE)?.value;

            if (selectedDate) {
                this.queryParams = this.queryParams
                    .set(WEEK, getISOWeek(selectedDate).toString())
                    .set(YEAR, selectedDate.getFullYear().toString());
            }
        }
    }

    getReports(): void {
        this.loadingUsers = true;
        this.setQueryParams();
        this.queryParams.set(COUNT, COUNT_ALL.toString());
        this.queryParams.set(OFFSET, OFFSET_START.toString());

        this.adminTimesheetService
            .get(['admin'], this.queryParams)
            .subscribe((response: Page<AdminTimesheet>) => {
                this.adminUserData.set(
                    response.items.sort((a, b) => a.name.localeCompare(b.name)),
                );
            });
    }

    exportTimesheets(reportType: string): void {
        const jsonBody = {
            UserProfileIds: this.selectedAdminUsers().map(
                (adminUser) => adminUser.userProfileId,
            ) as string[],
            Type: reportType,
        };

        this.setQueryParams();

        this.reportService
            .exportAdminReport(['adminUser/export'], jsonBody, this.queryParams)
            .subscribe({
                next: (buffer: Blob) => {
                    let mimeType: string = CSVMIMETYPE;
                    let fileExt: string = CSV;

                    if (reportType == PDF) {
                        mimeType = PDFMIMETYPE;
                        fileExt = PDF;
                    }

                    const data: Blob = new Blob([buffer], {
                        type: mimeType,
                    });

                    saveAs(data, `${ADMINUSERFILENAME}.${fileExt}`);

                    this.reportLoading = false;
                },
                error: () => {
                    this.reportLoading = false;
                    this.snackBar.open(MSG.FILE_EXPORT_F);
                },
            });
    }

    onExitClick(): void {
        this.dialogRef.close();
    }

    readonly separatorKeysCodes: number[] = [ENTER, COMMA];
    readonly announcer = inject(LiveAnnouncer);
    readonly currentUser = model('');
    readonly selectedAdminUsers = signal([]);
    readonly adminUserData = signal<AdminTimesheet[]>([]);
    readonly filteredAdminUsers = computed(() => {
        const currentName = this.currentUser().toLowerCase().trim();

        return this.adminUserData()
            .filter(
                (adminUser) =>
                    !this.selectedAdminUsers().some(
                        (selectedUser) =>
                            selectedUser.userProfileId ===
                            adminUser.userProfileId,
                    ),
            )
            .filter(
                (adminUser) =>
                    !currentName ||
                    adminUser.name.toLowerCase().includes(currentName),
            );
    });

    add(adminUser: MatChipInputEvent): void {
        const addedAdminUser = (adminUser.value || '').trim();

        if (!addedAdminUser) {
            return;
        }

        // Try to find a matching adminUser from adminusers
        const findMatchingAdminUser: AdminTimesheet = this.filteredAdminUsers().find(
            (adminUser) => adminUser.name.toLowerCase() === addedAdminUser.toLowerCase(),
        );

        if (findMatchingAdminUser) {
            // Only add user if not already selected
            const currentList: AdminTimesheet[] = this.selectedAdminUsers();
            if (
                !currentList.some(
                    (adminUser) =>
                        adminUser.userProfileId ===
                        findMatchingAdminUser.userProfileId,
                )
            ) {
                this.selectedAdminUsers.update((users) => [
                    ...users,
                    findMatchingAdminUser,
                ]);
            }
        }
    }

    remove(name: string): void {
        this.selectedAdminUsers.update((users) => {
            const index = users.indexOf(name);

            if (index < 0) {
                return users;
            }

            users.splice(index, 1);
            this.announcer.announce(`Removed ${name}`);
            return [...users];
        });
    }

    selected(event: MatAutocompleteSelectedEvent): void {
        const adminUser: AdminTimesheet = event.option.value;
        const currentList: AdminTimesheet[] = this.selectedAdminUsers();

        if (
            !currentList.some(
                (existingUser) =>
                    existingUser.userProfileId === adminUser.userProfileId,
            )
        ) {
            this.selectedAdminUsers.update((users) => [...users, adminUser]);
        }

        this.currentUser.set('');
    }
}
